// src/components/Navbar.jsx
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorMode,
  Heading,
  HStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import LogoutAlertDialog from "../UI/LogoutAlertDialog";
import {
  SunIcon,
  MoonIcon,
  UserIcon,
  ArrowLeftIcon,
} from "@phosphor-icons/react";
import { DEFAULT_WIDTH, HEADING_TITLE } from "../../helpers/constant";
import { useLocation, useNavigate } from "react-router-dom";
import LanguageMenu from "../UI/LanguageMenu";
import { useTranslation } from "react-i18next";
import { List } from "phosphor-react";
import { useNav } from "../../context/NavContext";
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";

export default function Navbar() {
  const { colorMode, toggleColorMode } = useColorMode();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggleNav } = useNav();
  const { signOutHandler } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const toast = useToast();

  // Theme-aware colors
  const bg = useColorModeValue("primary.500", "primary.800");
  const textColor = useColorModeValue("black", "white");

  const titleMap = {
    "/teachings": "menu.teachings",
    "/exhortations": "menu.exhortations",
    "/search": "menu.search",
    "/zoom-prayers": "menu.zoomPrayer",
    "/night-vigils": "menu.nightVigils",
    "/hymns": "menu.hymns",
    "/gallery": "menu.gallery",
    "/archives": "menu.archive",
    "/favorites": "menu.favorites",
    "/contact": "menu.contact",
    "/about-us": "menu.aboutUs",
    "/edit-profile": "common.editProfile",
    "/change-password": "common.changePassword",
    "/": "common.loading",
    "": "common.loading",
  };

  const titleKey = Object.keys(titleMap).find((path) => pathname === path);

  const title = t(
    titleMap[titleKey as keyof typeof titleMap] ?? "common.notFound",
  );

  useEffect(() => {
    document.title = `MSA FJC - ${title}`;
  }, [title]);

  const performSignOut = async () => {
    if (isSigningOut) return;

    setIsSigningOut(true);
    try {
      await signOutHandler();
      toast({
        title: String(t("profileMenu.signedOutTitle")),
        description: String(t("profileMenu.signedOutDescription")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
      navigate("/login", { replace: true });
    } catch (error) {
      toast({
        title: String(t("profileMenu.signOutErrorTitle")),
        description:
          error instanceof Error ? error.message : String(t("common.tryAgain")),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSigningOut(false);
      setIsLogoutDialogOpen(false);
    }
  };

  const canGoBackToPreviousPage =
    pathname === "/edit-profile" || pathname === "/change-password";

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/teachings");
  };

  // Array of menu items with paths
  const menuItems = [
    {
      label: t("profileMenu.updateProfile"),
      color: textColor,
      path: "/edit-profile", // navigate here when clicked
    },
    {
      label: t("profileMenu.changePassword"),
      path: "/change-password",
      color: textColor,
    },
    {
      label: t("profileMenu.logout"),
      path: "/login",
      color: "red.500",
      isDisabled: isSigningOut,
      onClick: async () => {
        if (!isSigningOut) {
          setIsLogoutDialogOpen(true);
        }
      },
    },
  ];

  return (
    <Box
      bgColor={{ base: bg, md: "transparent" }}
      px={{ base: "16px", lg: 6 }}
      py={{ base: 2, lg: 4 }}
      w="full"
      my={{ base: 0, lg: "24px" }}
      mt={{ md: "24px" }}
      position={{ base: "sticky", lg: "relative" }}
      top={{ base: 0, lg: "auto" }}
      zIndex="1000"
    >
      <Flex
        w={DEFAULT_WIDTH}
        mx="auto"
        align="center"
        color={{ base: "white", md: textColor }}
        justify="space-between"
      >
        <HStack display={{ base: "none", md: "flex" }} spacing="12px">
          {canGoBackToPreviousPage && (
            <IconButton
              aria-label={String(t("common.back"))}
              icon={<ArrowLeftIcon size={24} weight="bold" />}
              variant="ghost"
              color={textColor}
              onClick={handleBackNavigation}
            />
          )}
          <Heading
            fontSize={HEADING_TITLE}
            m={0}
            color={{ base: "white", md: textColor }}
          >
            {title}
          </Heading>
        </HStack>
        <HStack display={{ base: "flex", md: "none" }} spacing="12px">
          {canGoBackToPreviousPage ? (
            <IconButton
              aria-label={String(t("common.back"))}
              icon={<ArrowLeftIcon size={24} weight="bold" />}
              variant="ghost"
              color={{ base: "white", md: textColor }}
              onClick={handleBackNavigation}
            />
          ) : (
            <Box
              id="onboarding-mobile-menu"
              onClick={toggleNav}
              color={{ base: "white", md: textColor }}
              cursor="pointer"
            >
              <List size={24} weight="bold" />
            </Box>
          )}
          <Heading
            fontSize={HEADING_TITLE}
            color={{ base: "white", lg: "black" }}
            m={0}
            noOfLines={1}
          >
            {title}
          </Heading>
        </HStack>

        <Flex align="center" gap="4px" color={{ base: "white", md: textColor }}>
          {/* Theme Switch */}
          <IconButton
            id="onboarding-theme-toggle"
            aria-label="Toggle theme"
            icon={
              colorMode === "light" ? (
                <MoonIcon size={24} />
              ) : (
                <SunIcon size={24} />
              )
            }
            onClick={toggleColorMode}
            variant="ghost"
          />

          {/* Profile */}
          <Box id="onboarding-language-menu">
            <LanguageMenu />
          </Box>

          {/* Profile */}
          <Menu>
            <MenuButton
              id="onboarding-profile-menu"
              as={IconButton}
              icon={<UserIcon size={24} />}
              variant="ghost"
            />
            <MenuList>
              {menuItems.map((item, index) => (
                <MenuItem
                  key={index}
                  color={item.color}
                  isDisabled={item.isDisabled}
                  onClick={() => {
                    // If a custom onClick exists, call it; otherwise, navigate to path
                    if (item.onClick) {
                      item.onClick();
                    } else if (item.path) {
                      navigate(item.path);
                    }
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>
      </Flex>

      <LogoutAlertDialog
        isOpen={isLogoutDialogOpen}
        onClose={() => setIsLogoutDialogOpen(false)}
        onConfirm={performSignOut}
        isLoading={isSigningOut}
      />
    </Box>
  );
}
